/**
 * 顶部通知铃铛：最近通知 + 标记已处理。
 * 契约说明：PATCH /notifications/:id/read 语义为 markSent（status → SENT），
 * 后端无"已读"字段，UI 以"未处理 = PENDING"呈现。
 */
import { useEffect, useRef, useState } from 'react';
import { useMarkNotificationRead, useNotifications } from '../hooks/useMisc';
import { dateTimeStr, cn } from '../lib/utils';
import { Icon } from './icons';
import { Badge, Button, EmptyState, ErrorBox, Spinner } from './ui';

export function NotificationBell() {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const { data, isLoading, error } = useNotifications({ page: 1, pageSize: 10 });
  const markRead = useMarkNotificationRead();

  // 点击外部关闭
  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    };
    window.addEventListener('mousedown', onClick);
    return () => window.removeEventListener('mousedown', onClick);
  }, [open]);

  const pending = data?.items.filter((n) => n.status === 'PENDING').length ?? 0;

  return (
    <div ref={rootRef} className="relative">
      <button
        type="button"
        aria-label="通知"
        onClick={() => setOpen((v) => !v)}
        className="relative rounded-lg p-2 text-slate-500 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-800"
      >
        <Icon name="bell" />
        {pending > 0 && (
          <span className="absolute -right-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-rose-500 px-1 text-[10px] font-semibold text-white">
            {pending > 9 ? '9+' : pending}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 z-40 mt-2 w-80 rounded-xl border border-slate-200 bg-white shadow-lg dark:border-slate-700 dark:bg-slate-800">
          <div className="flex items-center justify-between border-b border-slate-200 px-4 py-2.5 dark:border-slate-700">
            <span className="text-sm font-semibold text-slate-800 dark:text-slate-100">通知</span>
            {pending > 0 && <Badge className="bg-indigo-100 text-indigo-700 dark:bg-indigo-900/50 dark:text-indigo-300">{pending} 条未处理</Badge>}
          </div>
          <div className="max-h-96 overflow-y-auto p-2">
            {isLoading ? (
              <div className="flex justify-center py-6">
                <Spinner />
              </div>
            ) : error ? (
              <ErrorBox error={error} className="m-2" />
            ) : !data || data.items.length === 0 ? (
              <EmptyState title="暂无通知" description="任务提醒会出现在这里" />
            ) : (
              data.items.map((n) => (
                <div
                  key={n.id}
                  className={cn(
                    'rounded-lg px-3 py-2 text-sm',
                    n.status === 'PENDING' ? 'bg-indigo-50/60 dark:bg-indigo-900/20' : '',
                  )}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <div className="truncate font-medium text-slate-800 dark:text-slate-100">{n.title}</div>
                      {n.body && <div className="mt-0.5 line-clamp-2 text-xs text-slate-500 dark:text-slate-400">{n.body}</div>}
                      <div className="mt-1 flex items-center gap-2 text-[11px] text-slate-400">
                        <span>{dateTimeStr(n.scheduledAt)}</span>
                        <Badge className={cn(
                          n.status === 'FAILED'
                            ? 'bg-rose-100 text-rose-700 dark:bg-rose-900/40 dark:text-rose-300'
                            : n.status === 'SENT'
                              ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300'
                              : 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300',
                        )}>
                          {n.status === 'FAILED' ? '失败' : n.status === 'SENT' ? '已处理' : '待处理'}
                        </Badge>
                      </div>
                    </div>
                    {n.status === 'PENDING' && (
                      <Button
                        size="sm"
                        variant="ghost"
                        loading={markRead.isPending && markRead.variables === n.id}
                        onClick={() => markRead.mutate(n.id)}
                      >
                        标记
                      </Button>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
